import testingPlan from '@/repositories/testing-plan.repository';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useGetTestingPlan() {
  return useQuery({
    queryKey: ['testing-plan', 'get'],
    queryFn: testingPlan.get,
  });
}

export function useGetCompletedTestingPlans(screeningTypeId: string | null = null) {
  return useQuery({
    queryKey: ['testing-plan', 'getHistory'],
    queryFn: () => testingPlan.getHistory(screeningTypeId),
  });
}

export function useGetTestResults(idTestResults: string) {
  return useQuery({
    queryKey: ['testing-plan', 'getTestResults', idTestResults],
    queryFn: () => testingPlan.getTestResults(idTestResults),
  });
}

export function useGetTestOptionDetails(idTestOption: string) {
  return useQuery({
    queryKey: ['testing-plan', 'getTestOptionDetails', idTestOption],
    queryFn: () => testingPlan.getTestOptionDetails(idTestOption),
  });
}

export function useGetTestingQuestionnaire(idTestOption: string) {
  return useQuery({
    queryKey: ['test-questionnaire', 'get'],
    queryFn: () => testingPlan.getTestQuestionnaire(idTestOption),
    gcTime: 0,
  });
}

export function useAnswerTestingQuestionnaire() {
  return useMutation({
    mutationFn: testingPlan.answerTestQuestionnaire,
  });
}

export function useDownloadDoctorLetter(testSuggestionId: string) {
  return useQuery({
    queryKey: ['testing-plan', 'downloadDoctorLetter', testSuggestionId],
    queryFn: async () => {
      const blob = await testingPlan.downloadDoctorLetter(testSuggestionId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `catch-doctor-letter-${testSuggestionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    enabled: false,
  });
}

export function useUpdateTestingPlanTargetDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ screeningId, targetDate }: { screeningId: string; targetDate: string | null }) =>
      testingPlan.updateTargetDate(screeningId, targetDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testing-plan', 'get'] });
    },
  });
}

export function useCompleteTestingPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (screeningId: string) => testingPlan.completeTest(screeningId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testing-plan', 'get'] });
      queryClient.invalidateQueries({ queryKey: ['testing-plan', 'getHistory'] });
    },
  });
}
